import { ref } from 'vue';
import { positionApi } from '@/entities/Position';
import { functionalActivityApi } from '@/entities/FunctionalActivity';

export interface PermissionSubject {
  id: number;
  name: string;
}

export type PermissionSubjectMode = 'positions' | 'functionalActivities';

const PAGE_SIZE = 1000;

export function usePermissionSubjects() {
  const positions = ref<PermissionSubject[]>([]);
  const functionalActivities = ref<PermissionSubject[]>([]);
  const isLoadingPositions = ref(false);
  const isLoadingFunctionalActivities = ref(false);
  const hasPositionsError = ref(false);
  const hasFunctionalActivitiesError = ref(false);

  const loadPositions = async () => {
    isLoadingPositions.value = true;
    hasPositionsError.value = false;
    try {
      const { data } = await positionApi.searchPositions({
        active: true,
        type: 'POSITION',
        sort: 'name',
        order: 'asc',
        page: 1,
        limit: PAGE_SIZE,
      });
      positions.value = data.map(({ id, name }) => ({ id, name }));
    } catch {
      hasPositionsError.value = true;
      positions.value = [];
    } finally {
      isLoadingPositions.value = false;
    }
  };

  const loadFunctionalActivities = async () => {
    isLoadingFunctionalActivities.value = true;
    hasFunctionalActivitiesError.value = false;
    try {
      const { data } = await functionalActivityApi.search({
        active: true,
        sort: 'name',
        order: 'asc',
        page: 1,
        limit: PAGE_SIZE,
      });
      functionalActivities.value = data.map(({ id, name }) => ({ id, name }));
    } catch {
      hasFunctionalActivitiesError.value = true;
      functionalActivities.value = [];
    } finally {
      isLoadingFunctionalActivities.value = false;
    }
  };

  const load = async () => {
    await Promise.all([loadPositions(), loadFunctionalActivities()]);
  };

  return {
    positions,
    functionalActivities,
    isLoadingPositions,
    isLoadingFunctionalActivities,
    hasPositionsError,
    hasFunctionalActivitiesError,
    load,
  };
}
